import { create } from 'zustand'
import { dbFetch, dbFetchJson } from '../lib/dbFetch'
import useAuthStore from './authStore'
import { supabase } from '../lib/supabase'

const mapReminder = (r) => r && ({
  id: r.id,
  userId: r.user_id,
  taskId: r.task_id,
  destinationAddress: r.destination_address,
  destinationLat: r.destination_lat,
  destinationLon: r.destination_lon,
  originAddress: r.origin_address,
  originLat: r.origin_lat,
  originLon: r.origin_lon,
  arriveBy: r.arrive_by,
  bufferMinutes: r.buffer_minutes,
  monitorWindowMinutes: r.monitor_window_minutes,
  status: r.status,
  lastCheckedAt: r.last_checked_at,
  lastTravelTimeSeconds: r.last_travel_time_seconds,
  lastTrafficDelaySeconds: r.last_traffic_delay_seconds,
  checkCount: r.check_count,
  alertSentAt: r.alert_sent_at,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
})

const useLocationReminderStore = create((set, get) => ({
  // { [taskId]: reminder | null }
  cache: {},
  alertedReminders: [],
  realtimeCleanup: null,

  loadForTask: async (taskId) => {
    if (!taskId) return null
    try {
      const reminders = await dbFetchJson(
        `location_reminders?task_id=eq.${taskId}&select=*`,
        'GET'
      )
      const reminder = reminders?.[0] ? mapReminder(reminders[0]) : null
      set((s) => ({ cache: { ...s.cache, [taskId]: reminder } }))
      return reminder
    } catch (err) {
      console.error('[locationReminderStore.loadForTask] ERROR:', err)
      throw err
    }
  },

  createForTask: async (taskId, data) => {
    if (!taskId) throw new Error('taskId required')
    const userId = useAuthStore.getState().user?.id
    if (!userId) throw new Error('not authenticated')

    try {
      console.log('[locationReminderStore.createForTask]', { taskId, data })
      const created = await dbFetchJson('location_reminders', 'POST', {
        user_id: userId,
        task_id: taskId,
        destination_address: data.destinationAddress,
        destination_lat: data.destinationLat,
        destination_lon: data.destinationLon,
        origin_address: data.originAddress,
        origin_lat: data.originLat,
        origin_lon: data.originLon,
        arrive_by: data.arriveBy,
        buffer_minutes: data.bufferMinutes ?? 5,
        monitor_window_minutes: data.monitorWindowMinutes ?? 90,
        status: 'scheduled',
      })
      console.log('[locationReminderStore.createForTask] created:', created)
      const reminder = mapReminder(created?.[0])
      set((s) => ({ cache: { ...s.cache, [taskId]: reminder } }))
      return reminder
    } catch (err) {
      console.error('[locationReminderStore.createForTask] ERROR:', err)
      throw err
    }
  },

  updateForTask: async (taskId, data) => {
    if (!taskId) throw new Error('taskId required')
    try {
      console.log('[locationReminderStore.updateForTask]', { taskId, data })
      const updated = await dbFetchJson(
        `location_reminders?task_id=eq.${taskId}`,
        'PATCH',
        {
          destination_address: data.destinationAddress,
          destination_lat: data.destinationLat,
          destination_lon: data.destinationLon,
          origin_address: data.originAddress,
          origin_lat: data.originLat,
          origin_lon: data.originLon,
          arrive_by: data.arriveBy,
          buffer_minutes: data.bufferMinutes ?? 5,
          monitor_window_minutes: data.monitorWindowMinutes ?? 90,
        }
      )
      console.log('[locationReminderStore.updateForTask] updated:', updated)
      const reminder = updated?.[0] ? mapReminder(updated[0]) : null
      set((s) => ({ cache: { ...s.cache, [taskId]: reminder } }))
      return reminder
    } catch (err) {
      console.error('[locationReminderStore.updateForTask] ERROR:', err)
      throw err
    }
  },

  deleteForTask: async (taskId) => {
    if (!taskId) throw new Error('taskId required')
    try {
      await dbFetch(`location_reminders?task_id=eq.${taskId}`, 'DELETE')
      set((s) => ({ cache: { ...s.cache, [taskId]: null } }))
    } catch (err) {
      console.error('[locationReminderStore.deleteForTask]', err)
      throw err
    }
  },

  dismissAlert: (reminderId) => {
    set((s) => ({
      alertedReminders: s.alertedReminders.filter((id) => id !== reminderId),
    }))
  },

  // Called by Realtime listener when status changes to 'alerted'
  onReminderAlerted: (reminder) => {
    const mapped = mapReminder(reminder)
    if (mapped) {
      set((s) => ({
        cache: { ...s.cache, [mapped.taskId]: mapped },
        alertedReminders: [...new Set([...s.alertedReminders, mapped.id])],
      }))
    }
  },

  initRealtime: () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) return

    const cleanup = get().realtimeCleanup
    if (cleanup) cleanup()

    const channel = supabase.channel(`store-location-reminders:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'location_reminders',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const reminder = payload.new ? mapReminder(payload.new) : null
          if (reminder?.taskId) {
            set((s) => ({
              cache: { ...s.cache, [reminder.taskId]: reminder },
            }))
            if (reminder.status === 'alerted' && !payload.old?.alert_sent_at) {
              set((s) => ({
                alertedReminders: [...new Set([...s.alertedReminders, reminder.id])],
              }))
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('[locationReminderStore] Realtime channel status:', status)
      })

    const destroyFn = () => {
      supabase.removeChannel(channel)
      set({ realtimeCleanup: null })
    }

    set({ realtimeCleanup: destroyFn })
  },

  destroyRealtime: () => {
    const cleanup = get().realtimeCleanup
    if (cleanup) cleanup()
  },
}))

export default useLocationReminderStore
